/**
 * Query-key factory for the Publication List view.
 *
 * Kept feature-scoped (inside `Publications/PublicationList/hooks/`) per the
 * SetupPDF convention. This can be promoted later if other features need to
 * invalidate the same keys.
 *
 * Shape: `['publicationList', 'list', { entityId, page, perPage, ... }]`
 *
 * Filter/sort/pagination params are embedded as a single object so a broad
 * invalidation `[...all, 'list']` matches every filter variant (used after
 * create/delete to refetch the active page).
 */

export type PublicationListQueryKeyParams = {
  entityId: number;
  page: number;
  perPage: number;
  publicationVisibility?: string;
  ogId?: number;
  shouldFilterByUser?: boolean;
  sort?: string;
  title?: string;
  storiesGranularAccessEnabled: boolean;
  isAdmin: boolean;
  userGroupIds: string[];
  subjectId: string;
};

export const publicationListQueryKeys = {
  all: ['publicationList'] as const,

  /** Paginated, filtered list of publications for an entity. */
  list: (params: PublicationListQueryKeyParams) =>
    [...publicationListQueryKeys.all, 'list', params] as const,

  /** Mutation key for create (POST /api/stories/v1/publications). */
  create: () => [...publicationListQueryKeys.all, 'create'] as const,

  /** Mutation key for delete (POST /api/stories/v1/publications/:id/delete). */
  delete: () => [...publicationListQueryKeys.all, 'delete'] as const
} as const;
